import { ReactNode } from "react";

import {
  isNotifyFrequency,
  NOTIFY_FREQUENCY,
  NotifyFrequency,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/notify-frequency";
import { DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

export const AlertsEmailDescription = ({ email }: { email: string }) => {
  return (
    <DialogDescription>
      We&apos;ll send job match emails to{" "}
      <span className="font-medium text-foreground">{email}</span>.
    </DialogDescription>
  );
};

export const AlertsGuestDescription = () => {
  return (
    <DialogDescription>
      Sign in to manage alerts and receive emails for new matches.
    </DialogDescription>
  );
};

export const AlertsMasterToggle = ({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          Email me about new jobs
        </p>
        <p className="text-xs text-ink-subtle">
          One digest per cadence, covering every search you turn on.
        </p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        data-testid="alerts-master-toggle"
        className="shrink-0"
      />
    </div>
  );
};

export const CadencePicker = ({
  value,
  onValueChange,
  disabled,
}: {
  value: NotifyFrequency;
  onValueChange: (value: NotifyFrequency) => void;
  disabled: boolean;
}) => {
  return (
    <div className="mt-3 flex items-center justify-between gap-3">
      <p className="text-xs font-medium text-ink-emphasis min-w-0">
        Send me a digest
      </p>
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(next) => {
          if (isNotifyFrequency(next)) onValueChange(next);
        }}
        variant="outline"
        disabled={disabled}
        className={cn("shrink-0", disabled && "opacity-50")}
        data-testid="alerts-frequency"
      >
        <ToggleGroupItem
          value={NOTIFY_FREQUENCY.daily}
          className="px-3 text-xs data-[state=on]:bg-surface-inverted data-[state=on]:text-primary-foreground"
        >
          Daily
        </ToggleGroupItem>
        <ToggleGroupItem
          value={NOTIFY_FREQUENCY.weekly}
          className="px-3 text-xs data-[state=on]:bg-surface-inverted data-[state=on]:text-primary-foreground"
        >
          Weekly
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};

export const AlertRow = ({
  title,
  subtitle,
  checked,
  enabled,
  onChange,
  icon,
}: {
  title: string;
  subtitle?: string;
  checked: boolean;
  enabled: boolean;
  onChange: (checked: boolean) => void;
  icon: ReactNode;
}) => {
  return (
    <div className="flex items-center gap-3 py-3 min-w-0">
      <AlertRowAvatar>{icon}</AlertRowAvatar>
      <AlertRowText title={title} subtitle={subtitle} />
      <AlertRowSwitch
        checked={checked}
        disabled={!enabled}
        onCheckedChange={onChange}
      />
    </div>
  );
};

export const AlertRowStatic = ({
  title,
  subtitle,
  icon,
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
}) => {
  return (
    <div className="flex items-center gap-3 py-3 min-w-0">
      <AlertRowAvatar>{icon}</AlertRowAvatar>
      <AlertRowText title={title} subtitle={subtitle} />
    </div>
  );
};

export const AlertRowAvatar = ({ children }: { children: ReactNode }) => {
  return (
    <span className="size-8 rounded-full bg-surface-strong flex items-center justify-center text-ink-emphasis shrink-0">
      {children}
    </span>
  );
};

export const AlertRowText = ({
  title,
  subtitle,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
}) => {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">{title}</p>
      {subtitle && (
        <p className="text-xs text-ink-subtle truncate">{subtitle}</p>
      )}
    </div>
  );
};

export const AlertRowSwitch = (props: React.ComponentProps<typeof Switch>) => {
  return <Switch {...props} className={cn("shrink-0", props.className)} />;
};
